import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function ScrollToTopButton() {
  const { t } = useTranslation("common");
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const buttonRef = useRef(null);
  const tickingRef = useRef(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    function getScrollY() {
      return (
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        0
      );
    }

    function isFooterTooClose() {
      const footer = document.querySelector("#paranoidi-footer");

      if (!footer || !buttonRef.current) {
        return false;
      }

      const footerTop = footer.getBoundingClientRect().top;
      const buttonBottom = buttonRef.current.getBoundingClientRect().bottom;

      return footerTop <= buttonBottom + 24;
    }

    function isMobileEditorActive() {
      const editor = document.querySelector("#paranoidi-product-editor");

      if (!editor) {
        return false;
      }

      const isMobileViewport = window.matchMedia(
        "(max-width: 1023px)"
      ).matches;

      if (!isMobileViewport) {
        return false;
      }

      const rect = editor.getBoundingClientRect();
      const viewportHeight =
        window.visualViewport?.height ||
        window.innerHeight ||
        document.documentElement.clientHeight;

      return rect.top < viewportHeight && rect.bottom > 0;
    }

function updateVisibility() {
  const currentScrollY = getScrollY();

  const hasScrolledEnough = currentScrollY > 180;
  const footerTooClose = isFooterTooClose();
  const mobileEditorActive = isMobileEditorActive();

  setIsVisible(
    hasScrolledEnough &&
      !footerTooClose &&
      !mobileEditorActive
  );
}

    function handleScroll() {
      if (tickingRef.current) {
        return;
      }

      tickingRef.current = true;

      window.requestAnimationFrame(() => {
        updateVisibility();
        tickingRef.current = false;
      });
    }
    updateVisibility();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", updateVisibility);

    window.visualViewport?.addEventListener(
      "resize",
      updateVisibility
    );

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", updateVisibility);

      window.visualViewport?.removeEventListener(
        "resize",
        updateVisibility
      );
    };
  }, []);

  if (!isMounted) {
    return null;
  }

  const button = (
    <button
      ref={buttonRef}
      type="button"
      onClick={() => {
        setIsVisible(false);

        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }}
      className={`fixed bottom-5 right-5 z-[2147483647] flex h-14 w-14 items-center justify-center rounded-full border border-red-400/45 bg-black/90 shadow-[0_0_35px_rgba(239,68,68,0.38)] backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-red-300 hover:bg-red-500/20 active:scale-95 sm:bottom-6 sm:right-6 ${
        isVisible
          ? "pointer-events-auto translate-y-0 opacity-100"
          : "pointer-events-none translate-y-4 opacity-0"
      }`}
      aria-label={t("navigation.backToTop")}
    >
      <span className="absolute inset-0 rounded-full bg-red-500/10" />

      <img
        src="/brand/scroll-to-top-icon.png"
        alt=""
        className="relative h-14 w-14 scale-[1.35] object-contain"
        aria-hidden="true"
      />

      <span
        aria-hidden="true"
        className="absolute -right-3 -top-2 rounded-full bg-red-500 px-2 py-0.5 text-sm font-black text-white shadow-lg ring-1 ring-yellow-400/90"
      >
        ↑
      </span>
    </button>
  );

  return createPortal(button, document.body);
}